import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";

/**
 * Order Filter Service - Handles filtering logic for order tables and lists
 */
export class OrderFilterService {
	/**
	 * Creates filters for order search functionality
	 * @param searchQuery Text search query
	 * @param statusKey Order status filter key
	 * @param postingDate Posting date filter
	 * @param deliveryDate Delivery date filter
	 * @returns Array of filters to apply
	 */
	createOrderFilters(
		searchQuery?: string,
		statusKey?: string,
		postingDate?: Date,
		deliveryDate?: Date,
	): Filter[] {
		const filters: Filter[] = [];

		// Text search filter
		if (searchQuery && searchQuery.trim() !== "") {
			filters.push(this.createTextSearchFilter(searchQuery));
		}

		// Order status filter
		if (statusKey) {
			const statusFilter = this.createStatusFilter(statusKey);
			if (statusFilter) {
				filters.push(statusFilter);
			}
		}

		// Date filters
		if (postingDate) {
			const isoString = this.convertToISODateString(postingDate);
			filters.push(new Filter("docDate", FilterOperator.EQ, isoString));
		}

		if (deliveryDate) {
			const isoString = this.convertToISODateString(deliveryDate);
			filters.push(new Filter("docDueDate", FilterOperator.EQ, isoString));
		}

		return filters;
	}

	/**
	 * Creates a text search filter for multiple order fields
	 * @param query Search query
	 * @returns Combined text search filter
	 */
	private createTextSearchFilter(query: string): Filter {
		return new Filter({
			filters: [
				new Filter("docEntry", FilterOperator.Contains, query),
				new Filter("docNum", FilterOperator.Contains, query),
				new Filter("user", FilterOperator.Contains, query),
				new Filter("cardCode", FilterOperator.Contains, query),
				new Filter("comments", FilterOperator.Contains, query),
			],
			and: false,
		});
	}

	/**
	 * Creates a status filter based on the status key
	 * @param statusKey Status key (YES/NO)
	 * @returns Status filter or null if no filter needed
	 */
	private createStatusFilter(statusKey: string): Filter | null {
		if (statusKey === "YES") {
			return new Filter("confirmed", FilterOperator.EQ, true);
		} else if (statusKey === "NO") {
			return new Filter("confirmed", FilterOperator.EQ, false);
		}
		return null;
	}

	/**
	 * Creates a filter for item name search
	 * @param searchValue Search value
	 * @returns Item name filter or empty array
	 */
	createItemNameFilter(searchValue: string): Filter[] {
		if (!searchValue || searchValue.trim() === "") {
			return [];
		}

		return [new Filter("itemName", FilterOperator.Contains, searchValue)];
	}

	private convertToISODateString(date: Date): string {
		// Use local date without timezone conversion to avoid date shifts
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const day = String(date.getDate()).padStart(2, "0");
		return `${year}-${month}-${day}T00:00:00.000Z`;
	}
}
